import { useForm } from 'app/hooks/form';
import { userInfoAtom } from 'app/atoms';
import { env } from 'app/lib/env';
import { useMyProfile } from 'app/network/profile';
import type { CreateProfileDTO } from 'app/types/profile';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { useAtomValue } from 'jotai/index';
import type { ImagePickerAsset } from 'expo-image-picker';

import * as yup from 'yup';

const isPhoneMandatory = true;

export function usePersonalInformationForm() {
  const profile = useMyProfile();
  const userInfo = useAtomValue(userInfoAtom);
  const schema: yup.ObjectSchema<CreateProfileDTO> = yup.object({
    additionalName: yup.string().max(255).trim().nullable().optional(),
    phone: yup
      .string()
      .trim()
      .defined()
      .transform((value: string) => {
        if (!value) return value;
        return value.replace(/[^+\d-. ]/g, '');
      })
      .optional()
      .when({
        is: () => isPhoneMandatory,
        then: (s) => s.required(),
        otherwise: (s) => s.optional(),
      }),
    picture: yup.mixed<ImagePickerAsset>().defined().optional(),
    firstName: yup.string().min(2).max(255).trim().required(),
    lastName: yup
      .string()
      .max(255)
      .trim()
      .when({
        is: () => isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags),
        then: (s) => s.min(2).required(),
        otherwise: (s) => s.optional(),
      }),
    bio: yup.string().optional().nullable(),
    groupIds: yup.array(yup.string().uuid().defined()).optional(),
  });

  const localDevDefaults =
    env.APP_ENV === 'local'
      ? {
          firstName: env.DEFAULT_FIRST_NAME ?? '',
          lastName: env.DEFAULT_LAST_NAME ?? '',
          phone: env.DEFAULT_PHONE ?? '',
        }
      : { firstName: '', lastName: '', phone: '' };

  const { control, handleSubmit } = useForm<CreateProfileDTO>({
    defaultValues: profile.data
      ? {
          additionalName: profile.data.additionalName ?? '',
          firstName: profile.data.firstName || localDevDefaults.firstName,
          lastName: profile.data.lastName || localDevDefaults.lastName,
          phone: profile.data.phone || localDevDefaults.phone,
          ...(profile.data.picture && { picture: { uri: profile.data.picture } }),
          bio: null,
        }
      : {
          additionalName: '',
          firstName: localDevDefaults.firstName,
          lastName: localDevDefaults.lastName,
          phone: localDevDefaults.phone,
          bio: null,
        },
    mode: 'onBlur',
    schema,
  });

  return { control, handleSubmit, userInfo };
}
